/**
 * TiroirNavigation.js - Navigation horizontale des tiroirs avec TIROIR 1, 2, 3, 4
 */

import { createElement } from "../../utils/dom.js";

export class TiroirNavigation {
  constructor(parentElement, options = {}) {
    if (!parentElement) {
      throw new Error("TiroirNavigation: parentElement est requis");
    }

    this.parentElement = parentElement;
    this.options = {
      activeTiroirs: options.activeTiroirs || [1, 2, 3, 4],
      onTiroirSelect: options.onTiroirSelect || (() => {}),
      onExportJSON: options.onExportJSON || (() => {}),
      onAbandon: options.onAbandon || (() => {}),
      ...options,
    };

    this.currentTiroir = this.options.activeTiroirs[0] || 1;
    this.tiroirStats = {};

    this.container = null;
    this.render();
    this.setupStatsListener();
  }

  /**
   * ✅ NOUVEAU : Écouter les changements de stats des tiroirs
   */
  setupStatsListener() {
    window.addEventListener("tiroirStatsChanged", (event) => {
      const { tiroirNum, stats } = event.detail;
      this.updateTiroirStats(tiroirNum, stats);
    });

    // : Initialiser les stats de tous les tiroirs au démarrage
    this.initializeAllTiroirStats();
  }

  /**
   * ✅ NOUVEAU : Initialiser les stats de tous les tiroirs
   */
  initializeAllTiroirStats() {
    // Attendre que les composants soient prêts
    setTimeout(() => {
      // Demander le calcul des stats pour tous les tiroirs actifs
      this.options.activeTiroirs.forEach((tiroirNum) => {
        // Déclencher un événement pour demander le calcul des stats
        window.dispatchEvent(
          new CustomEvent("requestTiroirStats", {
            detail: { tiroirNum },
          })
        );
      });
    }, 100); // Petit délai pour laisser les composants s'initialiser
  }

  /**
   * Créer le container du composant
   */
  createContainer() {
    this.container = createElement("div", {
      className: "tiroir-navigation",
    });

    this.parentElement.appendChild(this.container);
    return this.container;
  }

  /**
   * Mettre à jour les statistiques d'un tiroir
   */
  updateTiroirStats(tiroirNum, stats) {
    this.tiroirStats[tiroirNum] = stats;
    this.updateTabDisplay(tiroirNum);
  }

  /**
   * Mettre à jour l'affichage d'un tab
   */
  updateTabDisplay(tiroirNum) {
    const tab = this.container.querySelector(`[data-tiroir="${tiroirNum}"]`);
    if (tab && this.tiroirStats[tiroirNum]) {
      const percentageEl = tab.querySelector(".tab-percentage");
      if (percentageEl) {
        percentageEl.textContent = `${this.tiroirStats[tiroirNum].percentage || 0}%`;
      }
    }
  }

  /**
   * Calculer le pourcentage de completion d'un tiroir
   */
  calculateTiroirCompletion(tiroirNum) {
    // Cette méthode sera appelée depuis l'extérieur avec les vraies données
    return this.tiroirStats[tiroirNum]?.percentage || 0;
  }

  /**
   * Sélectionner un tiroir
   */
  selectTiroir(tiroirNum) {
    if (!this.options.activeTiroirs.includes(tiroirNum)) {
      return;
    }

    // Mettre à jour l'état interne
    this.currentTiroir = tiroirNum;

    // Mettre à jour l'affichage
    this.updateActiveTab();

    // : Rafraîchir les stats du tiroir sélectionné
    this.refreshTiroirStats(tiroirNum);

    // Notifier le changement
    this.options.onTiroirSelect(tiroirNum);
  }

  /**
   * ✅ NOUVEAU : Rafraîchir les stats d'un tiroir spécifique
   */
  refreshTiroirStats(tiroirNum) {
    // Demander le recalcul des stats pour ce tiroir
    window.dispatchEvent(
      new CustomEvent("requestTiroirStats", {
        detail: { tiroirNum },
      })
    );
  }

  /**
   * Mettre à jour l'affichage du tab actif
   */
  updateActiveTab() {
    // Retirer la classe active de tous les tabs
    const tabs = this.container.querySelectorAll(".nav-tab");
    tabs.forEach((tab) => {
      tab.classList.remove("nav-tab-active");
    });

    // Ajouter la classe active au tab courant
    const activeTab = this.container.querySelector(`[data-tiroir="${this.currentTiroir}"]`);
    if (activeTab) {
      activeTab.classList.add("nav-tab-active");
    }
  }

  /**
   * Mettre à jour la liste des tiroirs actifs
   */
  updateActiveTiroirs(newActiveTiroirs) {
    this.options.activeTiroirs = [...newActiveTiroirs];

    // Si le tiroir actuel n'est plus dans la liste, sélectionner le premier disponible
    if (!this.options.activeTiroirs.includes(this.currentTiroir)) {
      if (this.options.activeTiroirs.length > 0) {
        this.selectTiroir(this.options.activeTiroirs[0]);
      }
    }

    this.render();
  }

  /**
   * Rendu de la navigation
   */
  render() {
    if (!this.container) {
      this.createContainer();
    }

    this.container.innerHTML = `
      <div class="nav-container">
        <div class="nav-tabs">
          ${this.options.activeTiroirs
            .map((tiroirNum) => {
              const percentage = this.calculateTiroirCompletion(tiroirNum);
              const isActive = tiroirNum === this.currentTiroir;

              return `
              <button 
                class="nav-tab ${isActive ? "nav-tab-active" : ""}" 
                data-tiroir="${tiroirNum}"
              >
                <div class="tab-content">
                  <div class="tab-title">TIROIR ${tiroirNum}</div>
                  <div class="tab-percentage">${percentage}%</div>
                </div>
              </button>
            `;
            })
            .join("")}
        </div>
        
        <div class="nav-actions">
          <button class="nav-action-btn nav-export-btn" id="exportJSONBtn" title="Télécharger le projet en JSON">
            💾
          </button>
          
          <button class="nav-action-btn nav-delete-btn" id="deleteBtn" title="Supprimer toutes les données">
            🗑️
          </button>
        </div>
      </div>
    `;

    this.setupEventListeners();
  }

  /**
   * Configurer les événements
   */
  setupEventListeners() {
    const tabs = this.container.querySelectorAll(".nav-tab");

    tabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        const tiroirNum = parseInt(tab.dataset.tiroir);
        this.selectTiroir(tiroirNum);
      });

      // Effet hover subtil
      tab.addEventListener("mouseenter", () => {
        if (!tab.classList.contains("nav-tab-active")) {
          tab.style.transform = "translateY(-1px)";
        }
      });

      tab.addEventListener("mouseleave", () => {
        if (!tab.classList.contains("nav-tab-active")) {
          tab.style.transform = "translateY(0)";
        }
      });
    });

    // : Événements des boutons d'action
    const exportBtn = this.container.querySelector("#exportJSONBtn");
    const deleteBtn = this.container.querySelector("#deleteBtn");

    if (exportBtn) {
      exportBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (this.options.onExportJSON) {
          this.options.onExportJSON();
        } else {
          console.error("❌ Pas de callback onExportJSON");
        }
      });
    }

    if (deleteBtn) {
      deleteBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (this.options.onAbandon) {
          this.options.onAbandon();
        } else {
          console.error("❌ Pas de callback onAbandon");
        }
      });
    }
  }

  /**
   * Obtenir le tiroir actuellement sélectionné
   */
  getCurrentTiroir() {
    return this.currentTiroir;
  }

  /**
   * Obtenir la liste des tiroirs actifs
   */
  getActiveTiroirs() {
    return [...this.options.activeTiroirs];
  }

  /**
   * Animer l'apparition de la navigation
   */
  animate() {
    const tabs = this.container.querySelectorAll(".nav-tab");
    tabs.forEach((tab, index) => {
      setTimeout(() => {
        tab.style.opacity = "0";
        tab.style.transform = "translateY(-10px)";
        tab.style.transition = "all 0.4s ease-out";

        requestAnimationFrame(() => {
          tab.style.opacity = "1";
          tab.style.transform = "translateY(0)";
        });
      }, index * 100);
    });
  }

  /**
   * Obtenir l'élément DOM du composant
   */
  getElement() {
    return this.container;
  }

  /**
   * Vérifier si le composant est monté
   */
  isMounted() {
    return this.container && this.container.parentNode === this.parentElement;
  }

  /**
   * Démonter le composant
   */
  unmount() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }

  /**
   * Nettoyer les ressources
   */
  destroy() {
    // : Supprimer les écouteurs d'événements
    window.removeEventListener("tiroirStatsChanged", this.setupStatsListener);
    window.removeEventListener("requestTiroirStats", this.setupStatsListener);

    // Supprimer les événements
    const tabs = this.container?.querySelectorAll(".nav-tab");
    tabs?.forEach((tab) => {
      tab.removeEventListener("click", tab.onclick);
      tab.removeEventListener("mouseenter", tab.onmouseenter);
      tab.removeEventListener("mouseleave", tab.onmouseleave);
    });

    // Démonter le composant
    this.unmount();

    // Nettoyer les références
    this.container = null;
    this.parentElement = null;
    this.tiroirStats = {};
  }
}
